import { ClipboardList, MessageSquare, TrendingUp } from 'lucide-react';
import { useRevealOnScroll } from '../../ui';

const steps = [
  {
    icon: ClipboardList,
    step: '1',
    title: 'Start With A Real Baseline',
    description: 'Get a first week built around your goal, schedule, and current capacity instead of guessing where to begin.',
  },
  {
    icon: MessageSquare,
    step: '2',
    title: 'Check In As Life Happens',
    description: 'Log meals, workouts, soreness, travel, or questions as they happen so the plan can respond to real life.',
  },
  {
    icon: TrendingUp,
    step: '3',
    title: 'Adjust Before You Drift',
    description: 'Use what actually happened this week to tighten the next one before a few missed days turn into a lost month.',
  },
];

export function HowItWorks() {
  const gridRef = useRevealOnScroll<HTMLDivElement>();
  return (
    <section
      id="how-it-works"
      className="py-20"
      style={{ background: 'var(--color-surface-muted)' }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <p className="section-eyebrow">Week to Week</p>
          <h2 className="section-title">
            Coaching that stays useful after the first week
          </h2>
          <p className="section-copy mx-auto">
            The point is not one perfect plan. It is having a baseline, logging what actually happened, and adjusting quickly enough to keep momentum.
          </p>
        </div>

        <div
          ref={gridRef}
          className="reveal-on-scroll reveal-stagger grid grid-cols-1 gap-8 md:grid-cols-3 lg:gap-12"
        >
          {steps.map((step, index) => (
            <div key={step.title} className="relative">
              {index < steps.length - 1 && (
                <div
                  className="absolute left-1/2 top-12 hidden h-0.5 w-full md:block"
                  style={{ background: 'var(--color-accent-soft)' }}
                />
              )}
              <div className="app-panel relative p-8 text-center">
                <div
                  className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full"
                  style={{
                    background:
                      'linear-gradient(135deg, var(--color-brand), var(--color-brand-strong))',
                    boxShadow: 'var(--shadow-button)',
                    color: 'var(--color-on-brand)',
                  }}
                >
                  <step.icon className="h-8 w-8" />
                </div>
                <div
                  className="absolute -top-3 left-1/2 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full text-sm font-bold"
                  style={{
                    background: 'var(--color-accent)',
                    color: 'var(--color-on-brand)',
                    boxShadow: 'var(--shadow-button)',
                  }}
                >
                  {step.step}
                </div>
                <h3 className="mb-3 text-xl font-semibold" style={{ color: 'var(--color-ink)' }}>
                  {step.title}
                </h3>
                <p style={{ color: 'var(--color-muted)' }}>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
